import React, { useState, useEffect } from 'react';
import { IoClose, IoAdd, IoRemove, IoCart, IoFlash, IoTime } from 'react-icons/io5';
import { toast } from 'react-toastify';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Time Picker Modal Component
const TimePickerModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentTime = '12:00',
  title = 'Select Time',
}) => {
  const [selectedTime, setSelectedTime] = useState(currentTime);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
  const periods = ['AM', 'PM'];

  const [currentHour, currentMinute] = selectedTime.split(':');
  const currentPeriod = parseInt(currentHour) >= 12 ? 'PM' : 'AM';
  const displayHour = parseInt(currentHour) % 12 || 12;

  const handleHourChange = (hour) => {
    const actualHour =
      currentPeriod === 'PM' ? (hour === 12 ? 12 : hour + 12) : hour === 12 ? 0 : hour;
    setSelectedTime(`${actualHour.toString().padStart(2, '0')}:${currentMinute}`);
  };

  const handleMinuteChange = (minute) => {
    setSelectedTime(`${currentHour}:${minute.toString().padStart(2, '0')}`);
  };

  const handlePeriodChange = (period) => {
    const currentHourNum = parseInt(currentHour);
    let newHour = currentHourNum;

    if (period === 'AM' && currentHourNum >= 12) {
      newHour = currentHourNum - 12;
    } else if (period === 'PM' && currentHourNum < 12) {
      newHour = currentHourNum + 12;
    }

    setSelectedTime(`${newHour.toString().padStart(2, '0')}:${currentMinute}`);
  };

  const handleConfirm = () => {
    onConfirm(selectedTime);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
      <div className="bg-white p-6 rounded-2xl shadow-2xl w-80 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
          <IoClose size={20} />
        </button>

        <h3 className="text-lg font-semibold mb-4 pr-6 text-gray-800">{title}</h3>

        <div className="flex justify-between mb-6 space-x-3">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-600">Hour</label>
            <select
              value={displayHour}
              onChange={(e) => handleHourChange(parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            >
              {hours.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-600">Minute</label>
            <select
              value={currentMinute}
              onChange={(e) => handleMinuteChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            >
              {minutes.map((minute) => (
                <option key={minute} value={minute.toString().padStart(2, '0')}>
                  {minute.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 text-gray-600">AM/PM</label>
            <select
              value={currentPeriod}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-black focus:border-transparent transition-all"
            >
              {periods.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-center mb-4">
          <div className="text-xl font-bold bg-gray-50 p-3 rounded-lg border border-gray-200 text-gray-800">
            {`${displayHour}:${currentMinute} ${currentPeriod}`}
          </div>
        </div>

        <button
          onClick={handleConfirm}
          className="w-full py-2.5 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-all duration-200"
        >
          Confirm Time
        </button>
      </div>
    </div>
  );
};

const BookingModal = ({ formData, setFormData, selectedService, onClose, onBookingSuccess }) => {
  const [subcategories, setSubcategories] = useState([]);
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [isBooking, setIsBooking] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasBooked, setHasBooked] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [itemUrgency, setItemUrgency] = useState({});

  // Initialize form with default values
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const defaultTime = '12:00';

    setFormData((prev) => ({
      ...prev,
      scheduledtime: prev.scheduledtime || `${today}T${defaultTime}:00.000Z`,
      room_number: prev.room_number || '',
    }));
  }, [setFormData]);

  // Fetch all items once
  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-items/`);
        const data = await response.json();
        setAllItems(data);
      } catch (error) {
        console.error('Error fetching all items:', error);
        toast.error('Failed to load items. Please try again.');
      }
    };
    fetchAllItems();
  }, []);

  // Fetch Subcategories
  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-services/`);
        const data = await response.json();

        const filteredSubcategories = data.filter((sub) => sub.categoryid === selectedService?.id);
        setSubcategories(filteredSubcategories);

        if (filteredSubcategories.length > 0 && !selectedServiceId) {
          setSelectedServiceId(filteredSubcategories[0].id);
        }
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        toast.error('Failed to load services. Please try again.');
      }
    };

    if (selectedService) {
      fetchSubcategories();
    }
  }, [selectedService]);

  // Filter items when service is selected
  useEffect(() => {
    if (selectedServiceId) {
      const filteredItems = allItems.filter((item) => item.serviceid == selectedServiceId);
      setItems(filteredItems);

      const urgencyMap = {};
      filteredItems.forEach((item) => {
        urgencyMap[item.id] = 'Normal';
      });
      setItemUrgency(urgencyMap);
    } else if (selectedService?.id) {
      const filteredItems = allItems.filter((item) => item.categoryid === selectedService.id);
      setItems(filteredItems);

      const urgencyMap = {};
      filteredItems.forEach((item) => {
        urgencyMap[item.id] = 'Normal';
      });
      setItemUrgency(urgencyMap);
    }
  }, [selectedServiceId, allItems, selectedService]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (e) => {
    const value = e.target.value;

    setFormData((prev) => {
      const currentTime = prev.scheduledtime?.split('T')[1]?.substring(0, 5) || '12:00';
      return {
        ...prev,
        scheduledtime: `${value}T${currentTime}:00.000Z`,
      };
    });

    if (fieldErrors.date) {
      setFieldErrors((prev) => ({ ...prev, date: '' }));
    }
  };

  const handleTimeConfirm = (time) => {
    const [hours, minutes] = time.split(':');

    setFormData((prev) => {
      const currentDate =
        prev.scheduledtime?.split('T')[0] || new Date().toISOString().split('T')[0];
      return {
        ...prev,
        scheduledtime: `${currentDate}T${hours}:${minutes}:00.000Z`,
      };
    });

    if (fieldErrors.time) {
      setFieldErrors((prev) => ({ ...prev, time: '' }));
    }
  };

  const formatTimeForDisplay = (timeString) => {
    if (!timeString) return 'Select Time';

    try {
      const timePart = timeString.split('T')[1]?.substring(0, 5);
      if (!timePart) return 'Select Time';

      const [hours, minutes] = timePart.split(':');
      const hourNum = parseInt(hours);
      const period = hourNum >= 12 ? 'PM' : 'AM';
      const displayHour = hourNum % 12 || 12;

      return `${displayHour}:${minutes} ${period}`;
    } catch (error) {
      return 'Select Time';
    }
  };

  const handleUrgencyChange = (itemId, urgency) => {
    setItemUrgency((prev) => ({
      ...prev,
      [itemId]: urgency,
    }));
  };

  const handleAddToCart = (item, quantity) => {
    if (quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    const urgency = itemUrgency[item.id] || 'Normal';
    const price = urgency === 'Urgent' ? item.urgentprice : item.baseprice;

    const existingItem = cartItems.find((cartItem) => cartItem.itemid === item.id);

    if (existingItem) {
      const updatedCart = cartItems.map((cartItem) =>
        cartItem.itemid === item.id
          ? { ...cartItem, quantity: cartItem.quantity + quantity }
          : cartItem
      );
      setCartItems(updatedCart);
      toast.success(`Added ${quantity} more ${item.name}(s) to cart`);
    } else {
      const newItem = {
        itemid: item.id,
        item_name: item.name,
        quantity: quantity,
        urgencylevel: urgency,
        scheduledtime: formData.scheduledtime,
        specialinstructions: formData.specialinstructions || '',
        price: price,
        gst_value: item.value || 0,
        gst_type: item.value_type || 'P',
      };
      setCartItems([...cartItems, newItem]);
      toast.success(`${item.name} added to cart`);
    }
  };

  const handleRemoveFromCart = (itemid) => {
    setCartItems(cartItems.filter((item) => item.itemid !== itemid));
  };

  const handleUpdateCartQuantity = (itemid, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveFromCart(itemid);
      return;
    }
    setCartItems(
      cartItems.map((item) => (item.itemid === itemid ? { ...item, quantity: newQuantity } : item))
    );
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.room_number || formData.room_number.trim() === '') {
      errors.room_number = 'Room number is required';
    }

    if (cartItems.length === 0) {
      errors.cart = 'Please add at least one item to your order';
    }

    if (!formData.scheduledtime) {
      errors.date = 'Please select a date';
      errors.time = 'Please select a time';
    } else {
      const datePart = formData.scheduledtime.split('T')[0];
      const timePart = formData.scheduledtime.split('T')[1]?.substring(0, 5);

      if (!datePart) {
        errors.date = 'Please select a date';
      }
      if (!timePart || timePart === '00:00') {
        errors.time = 'Please select a time';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Calculate GST for an item
  const calculateItemGST = (item) => {
    const itemTotal = item.price * item.quantity;
    if (item.gst_type === 'P') {
      return (itemTotal * item.gst_value) / 100;
    }
    return item.gst_value; // Fixed amount
  };

  // Calculate totals with GST breakdown
  const calculateTotals = () => {
    let subtotal = 0;
    let totalGST = 0;

    cartItems.forEach((item) => {
      const itemTotal = item.price * item.quantity;
      subtotal += itemTotal;
      totalGST += calculateItemGST(item);
    });

    // GST is split equally between CGST and SGST (50-50)
    const cgst = totalGST / 2;
    const sgst = totalGST / 2;
    const grandTotal = subtotal + totalGST;

    return {
      subtotal,
      totalGST,
      cgst,
      sgst,
      grandTotal,
      gstRate: cartItems.length > 0 ? (totalGST / subtotal) * 100 : 0,
    };
  };

  const totals = calculateTotals();

  const handleBookNow = async () => {
    if (hasBooked || isBooking) {
      return;
    }

    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    const roomNumber = formData.room_number?.trim();
    if (!roomNumber) {
      toast.error('Room number is required');
      return;
    }

    setIsBooking(true);
    setHasBooked(true);

    try {
      const bulkItems = cartItems.map((item) => ({
        itemid: item.itemid,
        quantity: item.quantity,
        urgencylevel: item.urgencylevel,
        scheduledtime: formData.scheduledtime,
        specialinstructions: item.specialinstructions || 'No instructions',
      }));

      const requestData = {
        room_number: parseInt(roomNumber),
        items: bulkItems,
      };

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/room-service-requests/by-room/with-billing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create service requests and billing');
      }

      const responseData = await response.json();

      // Store booking details
      const bookingDetails = {
        bookingid: responseData.bookingid,
        room_number: responseData.room_number,
        guest_name: responseData.guest_name,
      };

      const billingDetails = {
        billingid: responseData.billingid,
        total_base: responseData.total_base,
        total_tax: responseData.total_tax,
        finalamount: responseData.total_final,
      };

      sessionStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
      sessionStorage.setItem('billingDetails', JSON.stringify(billingDetails));
      sessionStorage.setItem(
        'selectedService',
        JSON.stringify({
          ...selectedService,
          roomservices_name: selectedService?.name,
        })
      );

      if (onBookingSuccess) {
        onBookingSuccess({
          bookingDetails,
          billingDetails,
          response: responseData,
        });
      }

      // Reset cart
      setCartItems([]);

      // ✅ SHOW SUCCESS TOAST
      toast.success(
        <div>
          <p className="font-semibold text-lg">✅ Order Placed Successfully!</p>
          <p className="text-sm mt-1">Total: ₹{responseData.total_final}</p>
        </div>,
        {
          position: 'top-right',
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // ✅ Close modal AFTER toast is shown
      setTimeout(() => {
        setIsBooking(false);
        setHasBooked(false);
        onClose();
      }, 3500);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(`❌ Booking failed: ${error.message}`, {
        position: 'top-right',
        autoClose: 5000,
      });
      setHasBooked(false);
      setIsBooking(false);
    }
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const QuantitySelector = ({ item, onAdd }) => {
    const [qty, setQty] = useState(1);
    const urgency = itemUrgency[item.id] || 'Normal';

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setQty(Math.max(1, qty - 1))}
            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center justify-center transition-all"
          >
            <IoRemove size={16} />
          </button>
          <span className="w-8 text-center text-gray-800 font-semibold">{qty}</span>
          <button
            onClick={() => setQty(qty + 1)}
            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center justify-center transition-all"
          >
            <IoAdd size={16} />
          </button>
          <button
            onClick={() => {
              onAdd(item, qty);
              setQty(1);
            }}
            className="ml-1 px-4 py-1.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-all"
          >
            Add
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleUrgencyChange(item.id, 'Normal')}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all ${
              urgency === 'Normal'
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <IoTime size={12} />
            Normal
          </button>
          <button
            onClick={() => handleUrgencyChange(item.id, 'Urgent')}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all ${
              urgency === 'Urgent'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <IoFlash size={12} />
            Urgent
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 relative p-6 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedService?.name}</h2>
                <p className="text-gray-500 text-sm mt-1">Select items and add to order</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-100 rounded-lg"
              >
                <IoClose size={28} />
              </button>
            </div>
          </div>

          {/* Main Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
              {/* Items Section */}
              <div className="lg:col-span-2 space-y-4">
                {/* Room Number & Schedule */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Room Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="room_number"
                      value={formData.room_number || ''}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border ${
                        fieldErrors.room_number ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-black focus:border-transparent transition-all`}
                      placeholder="Enter room number"
                    />
                    {fieldErrors.room_number && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.room_number}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Service Type
                    </label>
                    <select
                      value={selectedServiceId}
                      onChange={(e) => setSelectedServiceId(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-800 focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                    >
                      {subcategories.map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.scheduledtime?.split('T')[0] || ''}
                      onChange={handleDateChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full px-4 py-3 border ${
                        fieldErrors.date ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl text-gray-800 focus:ring-2 focus:ring-black focus:border-transparent transition-all`}
                    />
                    {fieldErrors.date && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.date}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Time <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowTimePicker(true)}
                      className={`w-full px-4 py-3 border ${
                        fieldErrors.time ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl text-gray-800 text-left hover:bg-gray-50 transition-all`}
                    >
                      {formatTimeForDisplay(formData.scheduledtime)}
                    </button>
                    {fieldErrors.time && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.time}</p>
                    )}
                  </div>
                </div>

                {/* Items Grid */}
                <div>
                  <h3 className="text-gray-800 font-semibold mb-3 flex items-center gap-2">
                    <span className="text-black">✦</span> Available Items
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-gray-400 transition-all group"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="text-gray-800 font-medium">{item.name}</h4>
                            <p className="text-gray-600 text-sm">
                              ₹
                              {itemUrgency[item.id] === 'Urgent'
                                ? item.urgentprice
                                : item.baseprice}
                              {itemUrgency[item.id] === 'Urgent' && (
                                <span className="text-red-500 text-xs ml-1">(urgent)</span>
                              )}
                            </p>
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-full">
                              Available
                            </span>
                          </div>
                        </div>
                        <QuantitySelector item={item} onAdd={handleAddToCart} />
                      </div>
                    ))}
                  </div>
                  {items.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      No items available for this service
                    </div>
                  )}
                </div>
              </div>

              {/* Cart Section with GST Breakdown */}
              <div className="lg:col-span-1 bg-gray-50 rounded-xl border border-gray-200 p-4 h-fit sticky top-0">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-800 font-semibold flex items-center gap-2">
                    <IoCart className="text-black" />
                    Order Details
                  </h3>
                  <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-lg text-xs font-medium">
                    {getTotalItems()} items
                  </span>
                </div>

                {cartItems.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-3">🛒</div>
                    <p className="text-gray-500 text-sm">Cart is empty</p>
                    <p className="text-gray-400 text-xs mt-1">Add items from the list</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                      {cartItems.map((item) => (
                        <div
                          key={item.itemid}
                          className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="text-gray-800 text-sm font-medium">{item.item_name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-gray-600 text-xs">₹{item.price} each</p>
                                {item.urgencylevel === 'Urgent' && (
                                  <span className="text-red-500 text-[10px] font-semibold bg-red-50 px-2 py-0.5 rounded">
                                    Urgent
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveFromCart(item.itemid)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <IoClose size={16} />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() =>
                                handleUpdateCartQuantity(item.itemid, item.quantity - 1)
                              }
                              className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center text-sm transition-all"
                            >
                              -
                            </button>
                            <span className="text-gray-800 text-sm w-6 text-center font-medium">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                handleUpdateCartQuantity(item.itemid, item.quantity + 1)
                              }
                              className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center text-sm transition-all"
                            >
                              +
                            </button>
                            <span className="text-black font-semibold text-sm ml-auto">
                              ₹{item.price * item.quantity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 mt-4 pt-4 space-y-2">
                      {/* Subtotal */}
                      <div className="flex justify-between text-gray-700 text-sm font-medium border-t border-dashed border-gray-200 pt-1">
                        <span>Subtotal</span>
                        <span>₹{totals.subtotal.toFixed(2)}</span>
                      </div>

                      {/* GST Breakdown */}
                      {totals.totalGST > 0 && (
                        <>
                          <div className="flex justify-between text-gray-700 text-sm font-medium border-gray-200 ">
                            <span>Total GST</span>
                            <span>₹{totals.totalGST.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-gray-600 text-sm pl-4">
                            <span className="text-gray-500 text-xs">CGST</span>
                            <span className="text-gray-600">₹{totals.cgst.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-gray-600 text-sm pl-4">
                            <span className="text-gray-500 text-xs">SGST</span>
                            <span className="text-gray-600">₹{totals.sgst.toFixed(2)}</span>
                          </div>
                        </>
                      )}

                      {/* Grand Total */}
                      <div className="flex justify-between text-gray-800 font-bold text-lg pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span className="text-black">₹{totals.grandTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    {fieldErrors.cart && (
                      <p className="text-red-500 text-xs mt-2">{fieldErrors.cart}</p>
                    )}

                    <button
                      onClick={handleBookNow}
                      disabled={isBooking || hasBooked || cartItems.length === 0}
                      className={`w-full mt-4 py-3 rounded-xl font-semibold transition-all ${
                        isBooking || hasBooked || cartItems.length === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-black text-white hover:bg-gray-800 shadow-lg'
                      }`}
                    >
                      {isBooking ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        `Place Order (${getTotalItems()} items)`
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <TimePickerModal
        isOpen={showTimePicker}
        onClose={() => setShowTimePicker(false)}
        onConfirm={handleTimeConfirm}
        currentTime={formData.scheduledtime?.split('T')[1]?.substring(0, 5) || '12:00'}
        title="Select Schedule Time"
      />
    </>
  );
};

export default BookingModal;

// import React, { useState, useEffect } from 'react';
// import { IoClose } from 'react-icons/io5';
// import { toast, ToastContainer } from 'react-toastify';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Time Picker Modal Component
// const TimePickerModal = ({
//   isOpen,
//   onClose,
//   onConfirm,
//   currentTime = '12:00',
//   title = 'Select Time',
// }) => {
//   const [selectedTime, setSelectedTime] = useState(currentTime);

//   const hours = Array.from({ length: 12 }, (_, i) => i + 1); // 1-12
//   const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10, ..., 55
//   const periods = ['AM', 'PM'];

//   const [currentHour, currentMinute] = selectedTime.split(':');
//   const currentPeriod = parseInt(currentHour) >= 12 ? 'PM' : 'AM';
//   const displayHour = parseInt(currentHour) % 12 || 12;

//   const handleHourChange = (hour) => {
//     const actualHour =
//       currentPeriod === 'PM' ? (hour === 12 ? 12 : hour + 12) : hour === 12 ? 0 : hour;
//     setSelectedTime(`${actualHour.toString().padStart(2, '0')}:${currentMinute}`);
//   };

//   const handleMinuteChange = (minute) => {
//     setSelectedTime(`${currentHour}:${minute.toString().padStart(2, '0')}`);
//   };

//   const handlePeriodChange = (period) => {
//     const currentHourNum = parseInt(currentHour);
//     let newHour = currentHourNum;

//     if (period === 'AM' && currentHourNum >= 12) {
//       newHour = currentHourNum - 12;
//     } else if (period === 'PM' && currentHourNum < 12) {
//       newHour = currentHourNum + 12;
//     }

//     setSelectedTime(`${newHour.toString().padStart(2, '0')}:${currentMinute}`);
//   };

//   const handleConfirm = () => {
//     onConfirm(selectedTime);
//     onClose();
//   };

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] backdrop-blur-sm">
//       <div className="bg-white p-6 rounded-xl shadow-2xl w-80 relative">
//         {/* Close button */}
//         <button
//           onClick={onClose}
//           className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors duration-200"
//         >
//           <IoClose size={20} />
//         </button>

//         <h3 className="text-lg font-semibold mb-4 pr-6 text-gray-800">{title}</h3>

//         <div className="flex justify-between mb-6 space-x-3">
//           {/* Hour Selector */}
//           <div className="flex-1">
//             <label className="block text-sm font-medium mb-2 text-gray-700">Hour</label>
//             <select
//               value={displayHour}
//               onChange={(e) => handleHourChange(parseInt(e.target.value))}
//               className="w-full p-2 border border-gray-200 rounded-lg transition-all duration-200"
//             >
//               {hours.map((hour) => (
//                 <option key={hour} value={hour}>
//                   {hour}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Minute Selector */}
//           <div className="flex-1">
//             <label className="block text-sm font-medium mb-2 text-gray-700">Minute</label>
//             <select
//               value={currentMinute}
//               onChange={(e) => handleMinuteChange(e.target.value)}
//               className="w-full p-2 border border-gray-200 rounded-lg transition-all duration-200"
//             >
//               {minutes.map((minute) => (
//                 <option key={minute} value={minute.toString().padStart(2, '0')}>
//                   {minute.toString().padStart(2, '0')}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* AM/PM Selector */}
//           <div className="flex-1">
//             <label className="block text-sm font-medium mb-2 text-gray-700">AM/PM</label>
//             <select
//               value={currentPeriod}
//               onChange={(e) => handlePeriodChange(e.target.value)}
//               className="w-full p-2 border border-gray-200 rounded-lg transition-all duration-200"
//             >
//               {periods.map((period) => (
//                 <option key={period} value={period}>
//                   {period}
//                 </option>
//               ))}
//             </select>
//           </div>
//         </div>

//         {/* Display Selected Time */}
//         <div className="text-center mb-4">
//           <div className="text-xl font-bold bg-gray-50 p-3 rounded-lg border border-gray-200">
//             {`${displayHour}:${currentMinute} ${currentPeriod}`}
//           </div>
//         </div>

//         {/* Confirm Button */}
//         <div className="flex justify-end">
//           <button
//             onClick={handleConfirm}
//             className="w-full py-2.5 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200"
//           >
//             OK
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// const BookingModal = ({
//   formData,
//   setFormData,
//   selectedService,
//   onClose,
//   confirmServiceBooking,
// }) => {
//   const [subcategories, setSubcategories] = useState([]);
//   const [items, setItems] = useState([]);
//   const [allItems, setAllItems] = useState([]);
//   const [isBooking, setIsBooking] = useState(false);
//   const [showTimePicker, setShowTimePicker] = useState(false);
//   const [fieldErrors, setFieldErrors] = useState({});
//   const [hasBooked, setHasBooked] = useState(false); // Prevent double booking

//   // Initialize form with default values
//   useEffect(() => {
//     const today = new Date().toISOString().split('T')[0];
//     const defaultTime = '12:00';

//     setFormData((prev) => ({
//       ...prev,
//       scheduledtime: prev.scheduledtime || `${today}T${defaultTime}:00.000Z`,
//       quantity: prev.quantity || 1,
//       urgencylevel: prev.urgencylevel || 'Normal',
//       bookingid: prev.bookingid || '',
//     }));
//   }, [setFormData]);

//   // Fetch all items once when component mounts
//   useEffect(() => {
//     const fetchAllItems = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-items/`);
//         const data = await response.json();
//         setAllItems(data);
//       } catch (error) {
//         console.error('Error fetching all items:', error);
//         toast.error('Failed to load items. Please try again.');
//       }
//     };
//     fetchAllItems();
//   }, []);

//   // Fetch Subcategories (Services under the selected category)
//   useEffect(() => {
//     const fetchSubcategories = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/room-service-services/`);
//         const data = await response.json();

//         const filteredSubcategories = data.filter((sub) => sub.categoryid === selectedService?.id);

//         setSubcategories(filteredSubcategories);

//         if (filteredSubcategories.length > 0 && !formData.serviceid) {
//           setFormData((prev) => ({
//             ...prev,
//             serviceid: filteredSubcategories[0].id,
//           }));
//         }
//       } catch (error) {
//         console.error('Error fetching subcategories:', error);
//         toast.error('Failed to load services. Please try again.');
//       }
//     };

//     if (selectedService) {
//       fetchSubcategories();
//     }
//   }, [selectedService, setFormData, formData.serviceid]);

//   // Filter items when service is selected or when category changes
//   useEffect(() => {
//     if (formData.serviceid) {
//       const filteredItems = allItems.filter((item) => item.serviceid == formData.serviceid);
//       setItems(filteredItems);

//       if (filteredItems.length > 0 && !formData.itemid) {
//         setFormData((prev) => ({
//           ...prev,
//           itemid: filteredItems[0].id,
//         }));
//       }
//     } else if (selectedService?.id) {
//       const filteredItems = allItems.filter((item) => item.categoryid === selectedService.id);
//       setItems(filteredItems);

//       if (filteredItems.length > 0 && !formData.itemid) {
//         setFormData((prev) => ({
//           ...prev,
//           itemid: filteredItems[0].id,
//         }));
//       }
//     }
//   }, [formData.serviceid, allItems, selectedService, formData.itemid, setFormData]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));

//     if (fieldErrors[name]) {
//       setFieldErrors((prev) => ({ ...prev, [name]: '' }));
//     }
//   };

//   const handleDateChange = (e) => {
//     const value = e.target.value;

//     setFormData((prev) => {
//       const currentTime = prev.scheduledtime?.split('T')[1]?.substring(0, 5) || '12:00';
//       return {
//         ...prev,
//         scheduledtime: `${value}T${currentTime}:00.000Z`,
//       };
//     });

//     if (fieldErrors.date) {
//       setFieldErrors((prev) => ({ ...prev, date: '' }));
//     }
//   };

//   const handleTimeConfirm = (time) => {
//     const [hours, minutes] = time.split(':');

//     setFormData((prev) => {
//       const currentDate =
//         prev.scheduledtime?.split('T')[0] || new Date().toISOString().split('T')[0];
//       return {
//         ...prev,
//         scheduledtime: `${currentDate}T${hours}:${minutes}:00.000Z`,
//       };
//     });

//     if (fieldErrors.time) {
//       setFieldErrors((prev) => ({ ...prev, time: '' }));
//     }
//   };

//   const formatTimeForDisplay = (timeString) => {
//     if (!timeString) return 'Select Time';

//     try {
//       const timePart = timeString.split('T')[1]?.substring(0, 5);
//       if (!timePart) return 'Select Time';

//       const [hours, minutes] = timePart.split(':');
//       const hourNum = parseInt(hours);
//       const period = hourNum >= 12 ? 'PM' : 'AM';
//       const displayHour = hourNum % 12 || 12;

//       return `${displayHour}:${minutes} ${period}`;
//     } catch (error) {
//       return 'Select Time';
//     }
//   };

//   const validateForm = () => {
//     const errors = {};

//     if (!formData.bookingid || formData.bookingid.trim() === '') {
//       errors.bookingid = 'Reservation number is required';
//     }

//     if (!formData.serviceid) {
//       errors.serviceid = 'Please select a service';
//     }

//     if (!formData.itemid) {
//       errors.itemid = 'Please select an item';
//     }

//     if (!formData.quantity || formData.quantity < 1) {
//       errors.quantity = 'Please enter a valid quantity';
//     }

//     if (!formData.scheduledtime) {
//       errors.date = 'Please select a date';
//       errors.time = 'Please select a time';
//     } else {
//       const datePart = formData.scheduledtime.split('T')[0];
//       const timePart = formData.scheduledtime.split('T')[1]?.substring(0, 5);

//       if (!datePart) {
//         errors.date = 'Please select a date';
//       }
//       if (!timePart || timePart === '00:00') {
//         errors.time = 'Please select a time';
//       }
//     }

//     setFieldErrors(errors);
//     return Object.keys(errors).length === 0;
//   };

//   const handleBookNow = async () => {
//     // Prevent double booking
//     if (hasBooked || isBooking) {
//       return;
//     }

//     if (!validateForm()) {
//       toast.error('Please fill all required fields correctly');
//       return;
//     }

//     const bookingId = formData.bookingid?.trim();
//     if (!bookingId) {
//       toast.error('Reservation number is required');
//       return;
//     }

//     setIsBooking(true);
//     setHasBooked(true);

//     try {
//       // Create the room service request
//       const requestData = {
//         bookingid: bookingId,
//         categoryid: selectedService?.id,
//         serviceid: formData.serviceid,
//         itemid: formData.itemid,
//         quantity: parseInt(formData.quantity),
//         urgencylevel: formData.urgencylevel || 'Normal',
//         scheduledtime: formData.scheduledtime,
//         specialinstructions: formData.specialinstructions || 'No instructions',
//         status: 'Pending',
//       };

//       const requestResponse = await fetch(`${BQ_BASE_URL}/bq/api/room-service-requests/`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(requestData),
//       });

//       if (!requestResponse.ok) {
//         throw new Error('Failed to create service request');
//       }

//       // Create the billing - only once
//       const billingUrl = `${BQ_BASE_URL}/bq/api/create_room_service_billing?bookingid=${encodeURIComponent(bookingId)}&category_name=${encodeURIComponent(selectedService?.name || '')}`;

//       const billingResponse = await fetch(billingUrl, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (!billingResponse.ok) {
//         throw new Error('Failed to create billing');
//       }

//       const billingData = await billingResponse.json();

//       // Show success toast
//       toast.success(
//         <div>
//           <p>Service booked successfully!</p>
//           <p className="text-sm">Total amount: ₹{billingData.finalamount}</p>
//         </div>,
//         {
//           autoClose: 5000,
//           onClose: () => {
//             // Refresh the parent component if needed
//             if (confirmServiceBooking) {
//               confirmServiceBooking();
//             }
//             // Close the modal after toast is shown
//             onClose();
//           },
//         }
//       );
//     } catch (error) {
//       console.error('Booking error:', error);
//       toast.error(`Booking failed: ${error.message}`);
//       setHasBooked(false); // Reset booking state on error
//     } finally {
//       setIsBooking(false);
//     }
//   };

//   const getSelectedItemPrice = () => {
//     if (!formData.itemid) return null;
//     const selectedItem = items.find((item) => item.id == formData.itemid);
//     if (!selectedItem) return null;

//     return formData.urgencylevel === 'Urgent' ? selectedItem.urgentprice : selectedItem.baseprice;
//   };

//   return (
//     <>
//       {/* Main Booking Modal */}
//       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
//         <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-y-auto max-h-[90vh]">
//           <div className="p-6 relative">
//             <div className="flex justify-between items-start mb-3">
//               <div>
//                 <h2 className="text-2xl font-bold text-gray-800">Book {selectedService?.name}</h2>
//                 <p className="text-gray-500 text-sm mt-1">
//                   Fill in the details to schedule your service
//                 </p>
//               </div>
//               <button
//                 className="text-red-500 hover:text-red-700 transition-colors duration-200"
//                 onClick={onClose}
//               >
//                 <IoClose size={32} />
//               </button>
//             </div>

//             <div className="grid grid-cols-2 gap-3">
//               {/* Reservation Number */}
//               <div className="col-span-2">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Reservation number <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="text"
//                   name="bookingid"
//                   value={formData.bookingid || ''}
//                   onChange={handleInputChange}
//                   className={`w-full px-4 py-2.5 border rounded-lg duration-200 ${
//                     fieldErrors.bookingid ? 'border-red-500' : 'border-gray-200'
//                   }`}
//                   placeholder="Enter your reservation number"
//                 />
//                 {fieldErrors.bookingid && (
//                   <p className="text-red-500 text-xs mt-1">{fieldErrors.bookingid}</p>
//                 )}
//               </div>

//               {/* Service Dropdown */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Select a Service <span className="text-red-500">*</span>
//                 </label>
//                 <select
//                   name="serviceid"
//                   value={formData.serviceid || ''}
//                   onChange={handleInputChange}
//                   className={`w-full px-4 py-2.5 border rounded-lg duration-200 ${
//                     fieldErrors.serviceid ? 'border-red-500' : 'border-gray-200'
//                   }`}
//                 >
//                   <option value="">Select a service</option>
//                   {subcategories.map((sub) => (
//                     <option key={sub.id} value={sub.id}>
//                       {sub.name}
//                     </option>
//                   ))}
//                 </select>
//                 {fieldErrors.serviceid && (
//                   <p className="text-red-500 text-xs mt-1">{fieldErrors.serviceid}</p>
//                 )}
//               </div>

//               {/* Items Dropdown */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Select an Item <span className="text-red-500">*</span>
//                 </label>
//                 <select
//                   name="itemid"
//                   value={formData.itemid || ''}
//                   onChange={handleInputChange}
//                   className={`w-full px-4 py-2.5 border rounded-lg duration-200 ${
//                     fieldErrors.itemid ? 'border-red-500' : 'border-gray-200'
//                   }`}
//                 >
//                   <option value="">Select an item</option>
//                   {items.map((item) => (
//                     <option key={item.id} value={item.id}>
//                       {item.name} - ₹
//                       {formData.urgencylevel === 'Urgent' ? item.urgentprice : item.baseprice}
//                     </option>
//                   ))}
//                 </select>
//                 {fieldErrors.itemid && (
//                   <p className="text-red-500 text-xs mt-1">{fieldErrors.itemid}</p>
//                 )}
//                 {formData.itemid && getSelectedItemPrice() && (
//                   <p className="text-green-600 text-xs mt-1 font-medium">
//                     Price: ₹{getSelectedItemPrice()}
//                   </p>
//                 )}
//               </div>

//               {/* Urgency Level and Quantity */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Urgency Level
//                 </label>
//                 <select
//                   name="urgencylevel"
//                   value={formData.urgencylevel || 'Normal'}
//                   onChange={handleInputChange}
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                 >
//                   <option value="Normal">Normal</option>
//                   <option value="Urgent">Urgent (+20% charge)</option>
//                 </select>
//               </div>

//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Quantity <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="number"
//                   name="quantity"
//                   min="1"
//                   value={formData.quantity || 1}
//                   onChange={handleInputChange}
//                   className={`w-full px-4 py-2.5 border rounded-lg duration-200 ${
//                     fieldErrors.quantity ? 'border-red-500' : 'border-gray-200'
//                   }`}
//                 />
//                 {fieldErrors.quantity && (
//                   <p className="text-red-500 text-xs mt-1">{fieldErrors.quantity}</p>
//                 )}
//               </div>

//               {/* Schedule Date and Time */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Schedule Date <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="date"
//                   name="date"
//                   value={formData.scheduledtime?.split('T')[0] || ''}
//                   onChange={handleDateChange}
//                   min={new Date().toISOString().split('T')[0]}
//                   className={`w-full px-4 py-2.5 border rounded-lg duration-200 ${
//                     fieldErrors.date ? 'border-red-500' : 'border-gray-200'
//                   }`}
//                 />
//                 {fieldErrors.date && (
//                   <p className="text-red-500 text-xs mt-1">{fieldErrors.date}</p>
//                 )}
//               </div>

//               {/* Schedule Time with Clock Picker */}
//               <div className="col-span-1">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Schedule Time <span className="text-red-500">*</span>
//                 </label>
//                 <button
//                   type="button"
//                   onClick={() => setShowTimePicker(true)}
//                   className={`w-full px-4 py-2.5 border rounded-lg duration-200 text-left hover:bg-gray-50 transition-colors ${
//                     fieldErrors.time ? 'border-red-500' : 'border-gray-200'
//                   }`}
//                 >
//                   {formatTimeForDisplay(formData.scheduledtime)}
//                 </button>
//                 {fieldErrors.time && (
//                   <p className="text-red-500 text-xs mt-1">{fieldErrors.time}</p>
//                 )}
//               </div>

//               {/* Special Instructions */}
//               <div className="col-span-2">
//                 <label className="block text-gray-700 text-sm font-medium mb-1">
//                   Special Instructions
//                 </label>
//                 <textarea
//                   name="specialinstructions"
//                   value={formData.specialinstructions || ''}
//                   onChange={handleInputChange}
//                   rows="3"
//                   className="w-full px-4 py-2.5 border border-gray-200 rounded-lg duration-200"
//                   placeholder="Any special requests or instructions"
//                 />
//               </div>
//             </div>

//             {/* Buttons */}
//             <div className="mt-8 flex justify-end space-x-4">
//               <button
//                 onClick={onClose}
//                 className="px-10 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors duration-200"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleBookNow}
//                 disabled={isBooking || hasBooked}
//                 className={`px-6 py-2.5 text-white font-semibold rounded-lg transition-colors duration-200 bg-black hover:bg-gray-800 ${
//                   isBooking || hasBooked ? 'opacity-70 cursor-not-allowed' : ''
//                 }`}
//               >
//                 {isBooking ? (
//                   <span className="flex items-center justify-center">
//                     <svg
//                       className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
//                       xmlns="http://www.w3.org/2000/svg"
//                       fill="none"
//                       viewBox="0 0 24 24"
//                     >
//                       <circle
//                         className="opacity-25"
//                         cx="12"
//                         cy="12"
//                         r="10"
//                         stroke="currentColor"
//                         strokeWidth="4"
//                       ></circle>
//                       <path
//                         className="opacity-75"
//                         fill="currentColor"
//                         d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                       ></path>
//                     </svg>
//                     Processing...
//                   </span>
//                 ) : (
//                   'Book Now'
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Time Picker Modal */}
//       <TimePickerModal
//         isOpen={showTimePicker}
//         onClose={() => setShowTimePicker(false)}
//         onConfirm={handleTimeConfirm}
//         currentTime={formData.scheduledtime?.split('T')[1]?.substring(0, 5) || '12:00'}
//         title="Select Schedule Time"
//       />

//       <ToastContainer position="top-right" autoClose={5000} />
//     </>
//   );
// };

// export default BookingModal;
